import * as THREE from 'three';
import { StorageBufferAttribute } from 'three/webgpu';
import { assertStorageBufferBudget } from './bindingBudget';
import {
  CONTACT_RECORD_VEC4S,
  contactRecordHelpers,
} from './contactRecord';
import {
  createGpuOnlyStorageAttribute,
  type GpuOnlyStorageAllocation,
} from './gpuOnlyStorage';
import { qconj, qrot, worldInvInertia } from './quatUtils';
import { localId, storage, uniform, wgsl, wgslFn, workgroupId } from './tslCompat';

const WORKGROUP_SIZE = 64;
// impactState stores the epoch in f32. Keep it inside the exact-integer range
// so long-running simulations cannot silently stop advancing the epoch.
const MAX_EXACT_F32_EPOCH = 0x00ff_ffff;

export type RestitutionStageGpuContext = {
  renderer: any;
  device: GPUDevice;
};

export type RestitutionGpuOnlyBufferTelemetry = {
  gpuOnly: true;
  logicalVec4Count: number;
  cpuBytes: number;
  gpuBytes: number;
  destroyed: boolean;
};

export type RestitutionStageAllocationTelemetry = {
  disposed: boolean;
  totalCpuBytes: number;
  totalGpuBytes: number;
  impactState: RestitutionGpuOnlyBufferTelemetry;
  velocityScratch: RestitutionGpuOnlyBufferTelemetry;
  mechanicalEnergyTarget: RestitutionGpuOnlyBufferTelemetry;
};

/**
 * Optional coefficient-of-restitution velocity pass for AVBD.
 *
 * AVBD itself is position based and does not prescribe restitution. Call
 * initializeImpacts() immediately before AVBD velocity finalization, then call
 * solve() after finalization. The pass records the incoming contact velocity and
 * performs projected, accumulated normal-impulse Jacobi iterations afterwards.
 */
export class RestitutionStage {
  private readonly capturePhysicalVelocitiesKernel: any;
  private readonly correctConservativeEnergyKernel: any;
  private readonly initializeImpactsKernel: any;
  private readonly solveContactsKernel: any;
  private readonly gatherLinearVelocityKernel: any;
  private readonly gatherAngularVelocityKernel: any;
  private readonly commitVelocitiesKernel: any;

  private readonly impactStateAttr: StorageBufferAttribute;
  private readonly velocityScratchAttr: StorageBufferAttribute;
  private readonly mechanicalEnergyTargetAttr: StorageBufferAttribute;
  private readonly impactStateAllocation: GpuOnlyStorageAllocation<Float32Array>;
  private readonly velocityScratchAllocation: GpuOnlyStorageAllocation<Float32Array>;
  private readonly mechanicalEnergyTargetAllocation: GpuOnlyStorageAllocation<Float32Array>;
  private readonly maxBodies: number;
  private readonly maxPairContacts: number;

  private epoch = 0;
  private disposed = false;

  constructor(
    pairContacts: StorageBufferAttribute,
    pairActivity: StorageBufferAttribute,
    pairBodyContactCounts: StorageBufferAttribute,
    pairBodyContactIndices: StorageBufferAttribute,
    positions: StorageBufferAttribute,
    quaternions: StorageBufferAttribute,
    velocities: StorageBufferAttribute,
    angularVelocities: StorageBufferAttribute,
    inverseInertia: StorageBufferAttribute,
    conservativeBodyFlags: StorageBufferAttribute,
    maxBodies: number,
    maxPairContacts: number,
    maxPairContactsPerBody: number,
    pairActiveContactsOffset: number,
    pairActivityWordCount: number,
    gpuContext?: RestitutionStageGpuContext,
  ) {
    this.maxBodies = Math.max(0, Math.floor(maxBodies));
    this.maxPairContacts = Math.max(0, Math.floor(maxPairContacts));

    if (!gpuContext?.renderer || !gpuContext.device) {
      throw new Error(
        'RestitutionStage requires a materialized WebGPU renderer and GPUDevice '
        + 'to create its GPU-only scratch buffers.',
      );
    }

    const boundedMaxBodies = this.maxBodies;
    const boundedMaxPairContacts = this.maxPairContacts;
    const boundedMaxPairContactsPerBody = Math.max(0, Math.floor(maxPairContactsPerBody));
    const boundedPairActivityWordCount = Math.max(0, Math.floor(pairActivityWordCount));
    const boundedPairActiveContactsOffset = Math.max(0, Math.floor(pairActiveContactsOffset));
    const pairBodyFixedWords = boundedMaxBodies * boundedMaxPairContactsPerBody;
    const pairBodyOverflowHeadOffset = pairBodyFixedWords + 1;
    const pairBodyOverflowNodeOffset = pairBodyOverflowHeadOffset + boundedMaxBodies;
    const pairBodyOverflowCapacity = Math.floor(
      (pairBodyContactIndices.count - pairBodyOverflowNodeOffset) / 2,
    );
    const pairBodyContactRefHelpers = wgsl(/* wgsl */`
      fn loadRestitutionBodyContact(
        refs: ptr<storage, array<u32>, read>,
        body: u32,
        logicalIndex: u32,
        overflowNode: ptr<function, u32>,
      ) -> u32 {
        if (logicalIndex < ${boundedMaxPairContactsPerBody}u) {
          return refs[body * ${boundedMaxPairContactsPerBody}u + logicalIndex];
        }
        let node = *overflowNode;
        if (node == 0xffffffffu || node >= ${pairBodyOverflowCapacity}u) {
          return ${boundedMaxPairContacts}u;
        }
        let nodeBase = ${pairBodyOverflowNodeOffset}u + node * 2u;
        *overflowNode = refs[nodeBase + 1u];
        return refs[nodeBase];
      }
    `);

    // These buffers are written entirely by compute shaders. Materialize their
    // real GPUBuffers before creating any TSL storage binding, while retaining
    // only one vec4 placeholder per attribute on the CPU.
    let impactStateAllocation: GpuOnlyStorageAllocation<Float32Array> | undefined;
    let velocityScratchAllocation: GpuOnlyStorageAllocation<Float32Array> | undefined;
    let mechanicalEnergyTargetAllocation: GpuOnlyStorageAllocation<Float32Array> | undefined;
    try {
      // Per contact: target normal velocity, accumulated lambda, Jacobi delta
      // lambda, and the initialization epoch stored as an exact small f32.
      impactStateAllocation = createGpuOnlyStorageAttribute(
        gpuContext.renderer,
        gpuContext.device,
        {
          scalarType: 'float32',
          count: Math.max(1, boundedMaxPairContacts),
          itemSize: 4,
          label: 'AVBD Restitution Impact State (GPU-only)',
        },
      );

      // Two vec4 values per body: next linear and next angular velocity.
      velocityScratchAllocation = createGpuOnlyStorageAttribute(
        gpuContext.renderer,
        gpuContext.device,
        {
          scalarType: 'float32',
          count: Math.max(1, boundedMaxBodies) * 2,
          itemSize: 4,
          label: 'AVBD Restitution Velocity Scratch (GPU-only)',
        },
      );

      mechanicalEnergyTargetAllocation = createGpuOnlyStorageAttribute(
        gpuContext.renderer,
        gpuContext.device,
        {
          scalarType: 'float32',
          count: Math.max(1, boundedMaxBodies),
          itemSize: 4,
          label: 'Conservative Mechanical Energy Target (GPU-only)',
        },
      );
    } catch (error) {
      mechanicalEnergyTargetAllocation?.destroy();
      velocityScratchAllocation?.destroy();
      impactStateAllocation?.destroy();
      throw error;
    }

    this.impactStateAllocation = impactStateAllocation;
    this.velocityScratchAllocation = velocityScratchAllocation;
    this.mechanicalEnergyTargetAllocation = mechanicalEnergyTargetAllocation;
    this.impactStateAttr = impactStateAllocation.attribute;
    this.velocityScratchAttr = velocityScratchAllocation.attribute;
    this.mechanicalEnergyTargetAttr = mechanicalEnergyTargetAllocation.attribute;

    let constructionComplete = false;
    try {
    const capturePhysicalVelocitiesShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        conservativeBodyFlags: ptr<storage, array<u32>, read>,
        velocityScratch: ptr<storage, array<vec4f>, read_write>,
        mechanicalEnergyTarget: ptr<storage, array<vec4f>, read_write>,
        gravity: vec3f,
        dt: f32,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }
        let dynamicBody = positions[body].w > 0.0;
        let invMass = max(positions[body].w, 0.0);
        // WGSL select is not a control-flow guard; keep the inactive branch
        // finite as well so static bodies can never manufacture an Inf/NaN.
        let mass = select(0.0, 1.0 / max(invMass, 1e-12), invMass > 0.0);
        let linearVelocity = velocities[body].xyz;
        let angularVelocity = angularVelocities[body].xyz;
        let q = normalize(quaternions[body]);
        let localAngularVelocity = qrot(qconj(q), angularVelocity);
        let localInvInertia = max(inverseInertia[body].xyz, vec3f(0.0));
        let angularEnergy = 0.5 * (
          select(0.0, localAngularVelocity.x * localAngularVelocity.x / localInvInertia.x, localInvInertia.x > 1e-12)
          + select(0.0, localAngularVelocity.y * localAngularVelocity.y / localInvInertia.y, localInvInertia.y > 1e-12)
          + select(0.0, localAngularVelocity.z * localAngularVelocity.z / localInvInertia.z, localInvInertia.z > 1e-12)
        );
        let kineticEnergy = 0.5 * mass * dot(linearVelocity, linearVelocity) + angularEnergy;
        let conservativeIsland = conservativeBodyFlags[body];
        let conserveEnergy = dynamicBody
          && conservativeIsland > 0u
          && conservativeIsland != 0xffffffffu;
        mechanicalEnergyTarget[body] = select(
          vec4f(0.0),
          vec4f(kineticEnergy, positions[body].xyz),
          conserveEnergy,
        );
        velocityScratch[body * 2u] = select(
          vec4f(0.0),
          vec4f(velocities[body].xyz + gravity * dt, 0.0),
          dynamicBody,
        );
        velocityScratch[body * 2u + 1u] = select(
          vec4f(0.0),
          vec4f(angularVelocities[body].xyz, 0.0),
          dynamicBody,
        );
      }
    `, [qconj, qrot]);

    this.capturePhysicalVelocitiesKernel = capturePhysicalVelocitiesShader({
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      conservativeBodyFlags: storage(
        conservativeBodyFlags,
        'uint',
        boundedMaxBodies,
      ).toReadOnly(),
      velocityScratch: storage(
        this.velocityScratchAttr,
        'vec4f',
        Math.max(1, boundedMaxBodies) * 2,
      ),
      mechanicalEnergyTarget: storage(
        this.mechanicalEnergyTargetAttr,
        'vec4f',
        Math.max(1, boundedMaxBodies),
      ),
      gravity: uniform(new THREE.Vector3(0.0, -9.81, 0.0)),
      dt: uniform(1 / 60 / 4),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Capture Physical Velocities');
    assertStorageBufferBudget('Capture Physical Velocities', 8);

    const correctConservativeEnergyShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        conservativeBodyFlags: ptr<storage, array<u32>, read>,
        mechanicalEnergyTarget: ptr<storage, array<vec4f>, read>,
        gravity: vec3f,
        bodyCount: u32,
        islandCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let islandIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (islandIndex >= islandCount) { return; }
        let islandTag = islandIndex + 1u;
        var targetKinetic = 0.0;
        var currentKinetic = 0.0;
        var memberCount = 0u;

        for (var body = 0u; body < bodyCount; body++) {
          if (conservativeBodyFlags[body] != islandTag) { continue; }
          let invMass = max(positions[body].w, 0.0);
          if (invMass <= 0.0) { continue; }
          let mass = 1.0 / invMass;
          let linearVelocity = velocities[body].xyz;
          let angularVelocity = angularVelocities[body].xyz;
          let q = normalize(quaternions[body]);
          let localAngularVelocity = qrot(qconj(q), angularVelocity);
          let localInvInertia = max(inverseInertia[body].xyz, vec3f(0.0));
          let angularEnergy = 0.5 * (
            select(0.0, localAngularVelocity.x * localAngularVelocity.x / max(localInvInertia.x, 1e-12), localInvInertia.x > 1e-12)
            + select(0.0, localAngularVelocity.y * localAngularVelocity.y / max(localInvInertia.y, 1e-12), localInvInertia.y > 1e-12)
            + select(0.0, localAngularVelocity.z * localAngularVelocity.z / max(localInvInertia.z, 1e-12), localInvInertia.z > 1e-12)
          );
          let targetState = mechanicalEnergyTarget[body];
          targetKinetic += targetState.x
            + mass * dot(gravity, positions[body].xyz - targetState.yzw);
          currentKinetic += 0.5 * mass * dot(linearVelocity, linearVelocity) + angularEnergy;
          memberCount += 1u;
        }

        if (memberCount == 0u) { return; }
        targetKinetic = max(targetKinetic, 0.0);
        if (currentKinetic <= 1e-12) { return; }
        let scale = clamp(sqrt(targetKinetic / currentKinetic), 0.0, 4.0);

        for (var body = 0u; body < bodyCount; body++) {
          if (conservativeBodyFlags[body] != islandTag) { continue; }
          velocities[body] = vec4f(velocities[body].xyz * scale, velocities[body].w);
          angularVelocities[body] = vec4f(
            angularVelocities[body].xyz * scale,
            angularVelocities[body].w,
          );
        }
      }
    `, [qconj, qrot]);

    this.correctConservativeEnergyKernel = correctConservativeEnergyShader({
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      conservativeBodyFlags: storage(
        conservativeBodyFlags,
        'uint',
        boundedMaxBodies,
      ).toReadOnly(),
      mechanicalEnergyTarget: storage(
        this.mechanicalEnergyTargetAttr,
        'vec4f',
        Math.max(1, boundedMaxBodies),
      ).toReadOnly(),
      gravity: uniform(new THREE.Vector3(0.0, -9.81, 0.0)),
      bodyCount: uniform(0),
      islandCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Correct Conservative Constraint Energy');
    assertStorageBufferBudget('Correct Conservative Constraint Energy', 7);

    const initializeImpactsShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocityScratch: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read_write>,
        activeDispatchBound: u32,
        bodyCount: u32,
        restitution: f32,
        threshold: f32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${boundedPairActiveContactsOffset}u],
          activeDispatchBound,
        );
        if (gid >= activeCount) { return; }

        let p = pairActivity[${boundedPairActiveContactsOffset}u + gid + 1u];
        if (p >= ${boundedMaxPairContacts}u) { return; }

        let contactMeta = loadContactMeta(pairContacts, p);
        let bodyA = u32(contactMeta.x);
        let bodyB = u32(contactMeta.y);
        let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
        let normalLength2 = dot(normalRaw, normalRaw);
        if (
          contactMeta.z < 0.5
          || bodyA >= bodyCount
          || bodyB >= bodyCount
          || normalLength2 <= 1e-12
        ) {
          return;
        }

        let normal = normalRaw * inverseSqrt(normalLength2);
        let encodedInfo = bitcast<u32>(contactMeta.w);
        let preserveWarmstart = ((encodedInfo >> 16u) & 0x1u) != 0u;
        let qA = normalize(quaternions[bodyA]);
        let qB = normalize(quaternions[bodyB]);
        let armARecord = loadContactArmA(pairContacts, p);
        let armBRecord = loadContactArmB(pairContacts, p);
        let sphereArmFlags = u32(armBRecord.w + 0.5);
        let armA = select(
          qrot(qA, armARecord.xyz),
          armARecord.xyz,
          (sphereArmFlags & 1u) != 0u,
        );
        let armB = select(
          qrot(qB, armBRecord.xyz),
          armBRecord.xyz,
          (sphereArmFlags & 2u) != 0u,
        );
        let pointVelocityA = velocityScratch[bodyA * 2u].xyz
          + cross(velocityScratch[bodyA * 2u + 1u].xyz, armA);
        let pointVelocityB = velocityScratch[bodyB * 2u].xyz
          + cross(velocityScratch[bodyB * 2u + 1u].xyz, armB);
        let vnMinus = dot(normal, pointVelocityB - pointVelocityA);
        let newImpact = !preserveWarmstart && vnMinus < -threshold;
        let targetVelocity = select(0.0, max(-restitution * vnMinus, 0.0), newImpact);
        // Persistent rows stay in the velocity LCP with target=0, but only a
        // newly armed compression event receives the restitution target.
        impactState[p] = vec4f(targetVelocity, 0.0, 0.0, f32(epoch));
      }
    `, [qrot, contactRecordHelpers]);

    this.initializeImpactsKernel = initializeImpactsShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairActivity: storage(
        pairActivity,
        'uint',
        boundedPairActivityWordCount,
      ).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocityScratch: storage(
        this.velocityScratchAttr,
        'vec4f',
        Math.max(1, boundedMaxBodies) * 2,
      ).toReadOnly(),
      impactState: storage(this.impactStateAttr, 'vec4f', Math.max(1, boundedMaxPairContacts)),
      activeDispatchBound: uniform(0),
      bodyCount: uniform(0),
      restitution: uniform(0),
      threshold: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Initialize Restitution Impacts');
    assertStorageBufferBudget('AVBD Initialize Restitution Impacts', 5);

    const solveContactsShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read_write>,
        activeDispatchBound: u32,
        bodyCount: u32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${boundedPairActiveContactsOffset}u],
          activeDispatchBound,
        );
        if (gid >= activeCount) { return; }

        let p = pairActivity[${boundedPairActiveContactsOffset}u + gid + 1u];
        if (p >= ${boundedMaxPairContacts}u) { return; }

        var state = impactState[p];
        if (u32(state.w + 0.5) != epoch) { return; }

        let contactMeta = loadContactMeta(pairContacts, p);
        let bodyA = u32(contactMeta.x);
        let bodyB = u32(contactMeta.y);
        let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
        let normalLength2 = dot(normalRaw, normalRaw);
        if (
          contactMeta.z < 0.5
          || bodyA >= bodyCount
          || bodyB >= bodyCount
          || normalLength2 <= 1e-12
        ) {
          state.z = 0.0;
          impactState[p] = state;
          return;
        }

        let normal = normalRaw * inverseSqrt(normalLength2);
        let qA = normalize(quaternions[bodyA]);
        let qB = normalize(quaternions[bodyB]);
        let armARecord = loadContactArmA(pairContacts, p);
        let armBRecord = loadContactArmB(pairContacts, p);
        let sphereArmFlags = u32(armBRecord.w + 0.5);
        let armA = select(
          qrot(qA, armARecord.xyz),
          armARecord.xyz,
          (sphereArmFlags & 1u) != 0u,
        );
        let armB = select(
          qrot(qB, armBRecord.xyz),
          armBRecord.xyz,
          (sphereArmFlags & 2u) != 0u,
        );
        let angularJacobianA = -cross(armA, normal);
        let angularJacobianB = cross(armB, normal);

        let invMassA = max(positions[bodyA].w, 0.0);
        let invMassB = max(positions[bodyB].w, 0.0);
        let localInvInertiaA = select(
          vec3f(0.0),
          max(inverseInertia[bodyA].xyz, vec3f(0.0)),
          invMassA > 0.0,
        );
        let localInvInertiaB = select(
          vec3f(0.0),
          max(inverseInertia[bodyB].xyz, vec3f(0.0)),
          invMassB > 0.0,
        );
        let worldInvInertiaA = worldInvInertia(qA, localInvInertiaA);
        let worldInvInertiaB = worldInvInertia(qB, localInvInertiaB);
        let effectiveMass = invMassA + invMassB
          + dot(angularJacobianA, worldInvInertiaA * angularJacobianA)
          + dot(angularJacobianB, worldInvInertiaB * angularJacobianB);

        if (effectiveMass <= 1e-12) {
          state.z = 0.0;
          impactState[p] = state;
          return;
        }

        let pointVelocityA = velocities[bodyA].xyz
          + cross(angularVelocities[bodyA].xyz, armA);
        let pointVelocityB = velocities[bodyB].xyz
          + cross(angularVelocities[bodyB].xyz, armB);
        let vn = dot(normal, pointVelocityB - pointVelocityA);

        // Project the accumulated impulse, not the per-iteration increment.
        // lambda stays nonnegative; deltaLambda may be negative as Jacobi
        // revises an impulse applied in an earlier iteration.
        let oldLambda = max(state.y, 0.0);
        let newLambda = max(oldLambda + (state.x - vn) / effectiveMass, 0.0);
        state.y = newLambda;
        state.z = newLambda - oldLambda;
        impactState[p] = state;
      }
    `, [qrot, worldInvInertia, contactRecordHelpers]);

    this.solveContactsKernel = solveContactsShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairActivity: storage(
        pairActivity,
        'uint',
        boundedPairActivityWordCount,
      ).toReadOnly(),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(this.impactStateAttr, 'vec4f', Math.max(1, boundedMaxPairContacts)),
      activeDispatchBound: uniform(0),
      bodyCount: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Solve Restitution Contacts');
    assertStorageBufferBudget('AVBD Solve Restitution Contacts', 8);

    const gatherLinearVelocityShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        pairBodyContactIndices: ptr<storage, array<u32>, read>,
        positions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read>,
        velocityScratch: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }

        let invMass = max(positions[body].w, 0.0);
        var totalImpulse = vec3f(0.0);
        let contactCount = min(pairBodyContactCounts[body], 0xffffu);
        var contactOverflowNode = pairBodyContactIndices[
          ${pairBodyOverflowHeadOffset}u + body
        ];

        for (var slot = 0u; slot < contactCount; slot++) {
          let p = loadRestitutionBodyContact(
            pairBodyContactIndices, body, slot, &contactOverflowNode,
          );
          if (p >= ${boundedMaxPairContacts}u) { continue; }

          let state = impactState[p];
          if (u32(state.w + 0.5) != epoch || state.z == 0.0) { continue; }

          let contactMeta = loadContactMeta(pairContacts, p);
          let bodyA = u32(contactMeta.x);
          let bodyB = u32(contactMeta.y);
          if (contactMeta.z < 0.5 || (body != bodyA && body != bodyB)) { continue; }

          let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
          let normalLength2 = dot(normalRaw, normalRaw);
          if (normalLength2 <= 1e-12) { continue; }
          let normal = normalRaw * inverseSqrt(normalLength2);
          let sign = select(1.0, -1.0, body == bodyA);
          totalImpulse += sign * normal * state.z;
        }

        velocityScratch[body * 2u] = vec4f(
          velocities[body].xyz + invMass * totalImpulse,
          0.0,
        );
      }
    `, [contactRecordHelpers, pairBodyContactRefHelpers]);

    this.gatherLinearVelocityKernel = gatherLinearVelocityShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairBodyContactCounts: storage(
        pairBodyContactCounts,
        'uint',
        boundedMaxBodies,
      ).toReadOnly(),
      pairBodyContactIndices: storage(
        pairBodyContactIndices,
        'uint',
        pairBodyContactIndices.count,
      ).toReadOnly(),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(
        this.impactStateAttr,
        'vec4f',
        Math.max(1, boundedMaxPairContacts),
      ).toReadOnly(),
      velocityScratch: storage(this.velocityScratchAttr, 'vec4f', Math.max(1, boundedMaxBodies) * 2),
      bodyCount: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Gather Restitution Linear Velocity');
    assertStorageBufferBudget('AVBD Gather Restitution Linear Velocity', 7);

    const gatherAngularVelocityShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        pairBodyContactIndices: ptr<storage, array<u32>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read>,
        velocityScratch: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }

        let q = normalize(quaternions[body]);
        var totalTorqueImpulse = vec3f(0.0);
        let contactCount = min(pairBodyContactCounts[body], 0xffffu);
        var contactOverflowNode = pairBodyContactIndices[
          ${pairBodyOverflowHeadOffset}u + body
        ];

        for (var slot = 0u; slot < contactCount; slot++) {
          let p = loadRestitutionBodyContact(
            pairBodyContactIndices, body, slot, &contactOverflowNode,
          );
          if (p >= ${boundedMaxPairContacts}u) { continue; }

          let state = impactState[p];
          if (u32(state.w + 0.5) != epoch || state.z == 0.0) { continue; }

          let contactMeta = loadContactMeta(pairContacts, p);
          let bodyA = u32(contactMeta.x);
          let bodyB = u32(contactMeta.y);
          if (contactMeta.z < 0.5 || (body != bodyA && body != bodyB)) { continue; }

          let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
          let normalLength2 = dot(normalRaw, normalRaw);
          if (normalLength2 <= 1e-12) { continue; }
          let normal = normalRaw * inverseSqrt(normalLength2);
          let localArm = select(
            loadContactArmB(pairContacts, p).xyz,
            loadContactArmA(pairContacts, p).xyz,
            body == bodyA,
          );
          let sphereArmFlags = u32(loadContactArmB(pairContacts, p).w + 0.5);
          let armIsWorld = select(
            (sphereArmFlags & 2u) != 0u,
            (sphereArmFlags & 1u) != 0u,
            body == bodyA,
          );
          let arm = select(qrot(q, localArm), localArm, armIsWorld);
          let sign = select(1.0, -1.0, body == bodyA);
          totalTorqueImpulse += sign * cross(arm, normal) * state.z;
        }

        let localInvInertia = max(inverseInertia[body].xyz, vec3f(0.0));
        let deltaAngularVelocity = worldInvInertia(q, localInvInertia) * totalTorqueImpulse;
        velocityScratch[body * 2u + 1u] = vec4f(
          angularVelocities[body].xyz + deltaAngularVelocity,
          0.0,
        );
      }
    `, [qrot, worldInvInertia, contactRecordHelpers, pairBodyContactRefHelpers]);

    this.gatherAngularVelocityKernel = gatherAngularVelocityShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairBodyContactCounts: storage(
        pairBodyContactCounts,
        'uint',
        boundedMaxBodies,
      ).toReadOnly(),
      pairBodyContactIndices: storage(
        pairBodyContactIndices,
        'uint',
        pairBodyContactIndices.count,
      ).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(
        this.impactStateAttr,
        'vec4f',
        Math.max(1, boundedMaxPairContacts),
      ).toReadOnly(),
      velocityScratch: storage(this.velocityScratchAttr, 'vec4f', Math.max(1, boundedMaxBodies) * 2),
      bodyCount: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Gather Restitution Angular Velocity');
    assertStorageBufferBudget('AVBD Gather Restitution Angular Velocity', 8);

    const commitVelocitiesShader = wgslFn(/* wgsl */`
      fn compute(
        velocityScratch: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }
        velocities[body] = velocityScratch[body * 2u];
        angularVelocities[body] = velocityScratch[body * 2u + 1u];
      }
    `);

    this.commitVelocitiesKernel = commitVelocitiesShader({
      velocityScratch: storage(
        this.velocityScratchAttr,
        'vec4f',
        Math.max(1, boundedMaxBodies) * 2,
      ).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Commit Restitution Velocities');
    assertStorageBufferBudget('AVBD Commit Restitution Velocities', 3);
      constructionComplete = true;
    } finally {
      // A TSL graph/binding failure happens after the constructor has already
      // allocated the large raw buffers but before PhysicsEngine can own this
      // stage. Keep the allocation transaction closed here so retrying cannot
      // leak hundreds of MiB and turn a recoverable init error into an OOM.
      if (!constructionComplete) {
        mechanicalEnergyTargetAllocation.destroy();
        velocityScratchAllocation.destroy();
        impactStateAllocation.destroy();
      }
    }
  }

  /**
   * Shared two-vec4-per-body scratch used by the restitution gather/commit
   * passes. VelocityConstraintProjectionStage may reuse it because that stage
   * only runs inside the restitution branch and all accesses are ordered in
   * the same compute pass.
   */
  getVelocityScratchAttribute(): StorageBufferAttribute {
    if (this.disposed) {
      throw new Error('RestitutionStage velocity scratch is unavailable after dispose().');
    }
    return this.velocityScratchAttr;
  }

  getAllocationTelemetry(): RestitutionStageAllocationTelemetry {
    const describe = (
      allocation: GpuOnlyStorageAllocation<Float32Array>,
    ): RestitutionGpuOnlyBufferTelemetry => ({
      gpuOnly: true,
      logicalVec4Count: allocation.count,
      cpuBytes: allocation.attribute.array.byteLength,
      gpuBytes: allocation.gpuByteLength,
      destroyed: allocation.destroyed,
    });
    const impactState = describe(this.impactStateAllocation);
    const velocityScratch = describe(this.velocityScratchAllocation);
    const mechanicalEnergyTarget = describe(this.mechanicalEnergyTargetAllocation);
    return {
      disposed: this.disposed,
      totalCpuBytes: impactState.cpuBytes
        + velocityScratch.cpuBytes
        + mechanicalEnergyTarget.cpuBytes,
      totalGpuBytes: impactState.gpuBytes
        + velocityScratch.gpuBytes
        + mechanicalEnergyTarget.gpuBytes,
      impactState,
      velocityScratch,
      mechanicalEnergyTarget,
    };
  }

  /**
   * Destroys only the three raw GPUBuffers owned by this stage. The caller must
   * ensure previously submitted work has completed before invoking dispose().
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.mechanicalEnergyTargetAllocation.destroy();
    this.velocityScratchAllocation.destroy();
    this.impactStateAllocation.destroy();
  }

  capturePhysicalVelocities(
    renderer: any,
    bodyCount: number,
    gravity: readonly [number, number, number],
    dt: number,
  ): void {
    if (this.disposed) return;
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    if (boundedBodyCount === 0) return;
    const parameters = this.capturePhysicalVelocitiesKernel.computeNode.parameters;
    parameters.bodyCount.value = boundedBodyCount;
    parameters.dt.value = Math.max(dt, 1e-6);
    parameters.gravity.value.set(gravity[0], gravity[1], gravity[2]);
    renderer.compute(this.capturePhysicalVelocitiesKernel, [
      Math.ceil(boundedBodyCount / WORKGROUP_SIZE),
      1,
      1,
    ]);
  }

  restorePhysicalVelocities(renderer: any, bodyCount: number): void {
    if (this.disposed) return;
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    if (boundedBodyCount === 0) return;
    this.commitVelocitiesKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;
    renderer.compute(this.commitVelocitiesKernel, [
      Math.ceil(boundedBodyCount / WORKGROUP_SIZE),
      1,
      1,
    ]);
  }

  correctConservativeEnergies(
    renderer: any,
    bodyCount: number,
    islandCount: number,
    gravity: readonly [number, number, number],
  ): void {
    if (this.disposed) return;
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    const boundedIslandCount = Math.min(
      boundedBodyCount,
      Math.max(0, Math.floor(islandCount)),
    );
    if (boundedBodyCount === 0 || boundedIslandCount === 0) return;
    const parameters = this.correctConservativeEnergyKernel.computeNode.parameters;
    parameters.bodyCount.value = boundedBodyCount;
    parameters.islandCount.value = boundedIslandCount;
    parameters.gravity.value.set(gravity[0], gravity[1], gravity[2]);
    renderer.compute(this.correctConservativeEnergyKernel, [
      Math.ceil(boundedIslandCount / WORKGROUP_SIZE),
      1,
      1,
    ]);
  }

  initializeImpacts(
    renderer: any,
    activeDispatchBound: number,
    bodyCount: number,
    restitution: number,
    threshold: number,
  ): void {
    if (this.disposed) return;
    const boundedActiveDispatch = Math.min(
      this.maxPairContacts,
      Math.max(0, Math.floor(activeDispatchBound)),
    );
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    const boundedRestitution = Math.min(1.0, Math.max(0.0, restitution));
    const boundedThreshold = Math.max(0.0, threshold);

    this.epoch = this.epoch >= MAX_EXACT_F32_EPOCH ? 1 : this.epoch + 1;

    if (boundedActiveDispatch === 0 || boundedBodyCount === 0) return;

    const parameters = this.initializeImpactsKernel.computeNode.parameters;
    parameters.activeDispatchBound.value = boundedActiveDispatch;
    parameters.bodyCount.value = boundedBodyCount;
    parameters.restitution.value = boundedRestitution;
    parameters.threshold.value = boundedThreshold;
    parameters.epoch.value = this.epoch;

    renderer.compute(this.initializeImpactsKernel, [
      Math.ceil(boundedActiveDispatch / WORKGROUP_SIZE),
      1,
      1,
    ]);
  }

  solve(
    renderer: any,
    activeDispatchBound: number,
    bodyCount: number,
    iterations: number,
  ): void {
    if (this.disposed) return;
    const boundedActiveDispatch = Math.min(
      this.maxPairContacts,
      Math.max(0, Math.floor(activeDispatchBound)),
    );
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    const boundedIterations = Number.isFinite(iterations)
      ? Math.max(0, Math.floor(iterations))
      : 0;

    if (
      this.epoch === 0
      || boundedActiveDispatch === 0
      || boundedBodyCount === 0
      || boundedIterations === 0
    ) {
      return;
    }

    const contactParameters = this.solveContactsKernel.computeNode.parameters;
    contactParameters.activeDispatchBound.value = boundedActiveDispatch;
    contactParameters.bodyCount.value = boundedBodyCount;
    contactParameters.epoch.value = this.epoch;

    const linearParameters = this.gatherLinearVelocityKernel.computeNode.parameters;
    linearParameters.bodyCount.value = boundedBodyCount;
    linearParameters.epoch.value = this.epoch;

    const angularParameters = this.gatherAngularVelocityKernel.computeNode.parameters;
    angularParameters.bodyCount.value = boundedBodyCount;
    angularParameters.epoch.value = this.epoch;

    this.commitVelocitiesKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;

    const roundKernels: any[] = [];
    for (let iteration = 0; iteration < boundedIterations; iteration++) {
      roundKernels.push(
        this.solveContactsKernel,
        this.gatherLinearVelocityKernel,
        this.gatherAngularVelocityKernel,
        this.commitVelocitiesKernel,
      );
    }

    const dispatchWorkgroups = Math.ceil(
      Math.max(boundedActiveDispatch, boundedBodyCount) / WORKGROUP_SIZE,
    );
    renderer.compute(roundKernels, [dispatchWorkgroups, 1, 1]);
  }

  async readActiveDebugStates(renderer: any): Promise<Array<{
    contact: number;
    target: number;
    lambda: number;
    deltaLambda: number;
    storedEpoch: number;
  }> | null> {
    if (
      this.disposed
      || !renderer
      || typeof renderer.getArrayBufferAsync !== 'function'
      || this.epoch === 0
    ) {
      return null;
    }
    const raw = await renderer.getArrayBufferAsync(this.impactStateAttr);
    const values = new Float32Array(raw);
    const result: Array<{ contact: number; target: number; lambda: number; deltaLambda: number; storedEpoch: number }> = [];
    for (let contact = 0; contact < this.maxPairContacts; contact++) {
      const base = contact * 4;
      const storedEpoch = values[base + 3] ?? 0;
      if (
        storedEpoch === 0
        && (values[base] ?? 0) === 0
        && (values[base + 1] ?? 0) === 0
        && (values[base + 2] ?? 0) === 0
      ) continue;
      result.push({
        contact,
        target: values[base] ?? 0,
        lambda: values[base + 1] ?? 0,
        deltaLambda: values[base + 2] ?? 0,
        storedEpoch,
      });
    }
    return result;
  }
}
